import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { refineContractRequiredMinimum } from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { getInvestmentToolSchema } from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolSchema";
import { getManualValueShape } from "web_myplann/Portfolio/PortfolioShared/getManualValueShape";
import { refineManualValue } from "web_myplann/Portfolio/PortfolioShared/refineManualValue";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

export const getInvestmentBaseShape = (t: TranslateFunction) => ({
	name: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	contractNumber: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.Name,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.Name,
			}),
		),
	),
	producer: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	product: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	signedAt: optionalDateAsString(DateFormat.Iso),
	startAt: optionalDateAsString(DateFormat.Iso),
	endAt: optionalDateAsString(DateFormat.Iso),
	tools: z.array(getInvestmentToolSchema(t)),
});

export const refineInvestmentDates = (t: TranslateFunction) =>
	refineDateOrder({
		startBeforeSigned: t(
			Resources.Portfolio.AddShared.Validations.startBeforeSigned,
		),
		endBeforeSigned: t(
			Resources.Portfolio.AddShared.Validations.endBeforeSigned,
		),
	});

export const getInvestmentCreateFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getInvestmentBaseShape(t),
			...getManualValueShape(t),
		})
		.superRefine(refineManualValue(t))
		.superRefine(refineInvestmentDates(t))
		.superRefine(refineContractRequiredMinimum);

export const getInvestmentEditFormSchema = (t: TranslateFunction) =>
	z
		.object(getInvestmentBaseShape(t))
		.superRefine(refineInvestmentDates(t))
		.superRefine(refineContractRequiredMinimum);

export type InvestmentCreateFormInput = z.input<
	ReturnType<typeof getInvestmentCreateFormSchema>
>;
export type InvestmentCreateFormOutput = z.output<
	ReturnType<typeof getInvestmentCreateFormSchema>
>;
export type InvestmentEditFormInput = z.input<
	ReturnType<typeof getInvestmentEditFormSchema>
>;
export type InvestmentEditFormOutput = z.output<
	ReturnType<typeof getInvestmentEditFormSchema>
>;
